import { Component } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { NacoService } from '../../domains/services/naco.service';
import { Storage } from '@ubud/storage';
import { concat, from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Location } from '@angular/common';

@Component({
    selector: 'naker-auth-page',
    template: '{{ message$ | async }}',
})
export class AuthPage {
    public message$: Observable<string>;

    public constructor(private route: ActivatedRoute, private naco: NacoService, private storage: Storage, private location: Location) {
        this.message$ = concat(of('Authenticating...'), this.handle());
    }

    public handle(): Observable<string> {
        return from(this.naco.getState()).pipe(
            switchMap(state => {
                return this.route.queryParams.pipe(
                    switchMap(params => {
                        if (state && params.state !== state) {
                            return throwError('Invalid CSRF Token');
                        }

                        return of(params);
                    }),
                );
            }),
            switchMap((params: Params) => {
                this.naco.setUser(null);

                if (params.access_token) {
                    this.naco.setSignature({
                        expiresIn: params.expires_in,
                        type: params.token_type,
                        token: params.access_token,
                        scopes: decodeURIComponent(params.scopes),
                    });

                    return this.naco.getUser();
                }

                if (params.code) {
                    return this.naco.getAccessTokenFromAuthCode(params.code, this.naco.getCurrentOrigin('/auth')).pipe(
                        tap((tokens: any) => {
                            this.naco.setSignature({
                                expiresIn: tokens.expires_in || tokens.expiresIn,
                                type: tokens.token_type || tokens.tokenType,
                                token: tokens.access_token || tokens.accessToken,
                                scopes: decodeURIComponent(tokens.scopes),
                            });

                            this.naco.setRefreshToken({
                                expiresIn: tokens.expires_in || tokens.expiresIn,
                                type: tokens.token_type || tokens.tokenType,
                                token: tokens.refresh_token || tokens.refreshToken,
                                scopes: decodeURIComponent(tokens.scopes),
                            });
                        }),
                        switchMap(() => this.naco.getUser()),
                    );
                }

                return of(null);
            }),
            switchMap(user => {
                if (null === user) {
                    return throwError(new Error('Unauthenticated!'));
                }

                this.naco.goToIntendedUrl();

                return of('Success, please wait for redirecting...');
            }),
            catchError(e => {
                return of(e.message);
            }),
        );
    }
}
